// Illustration service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (illustration_posting_params)
//   validation app/models/job_postings/job_postings/step_validations.rb (illustration__one..five)
//   cost       app/models/job_postings/illustration_posting.rb (#cost / #add_ons_cost)
//   parity     tasks/post-a-project/parity/design-media.md §2
//
// page_size MUST submit one of the 15 quote-formatted ILLUSTRATION_SIZES (e.g. '5" X 8"');
// the design's 6 plain values ('5x8') hard-fail value_in_array(ILLUSTRATION_SIZE_VALUES).

import {
  NICHE_AND_GENRE_GROUPS,
  ILLUSTRATION_INTERIOR_TYPES,
  ILLUSTRATION_SIZES,
  ILLUSTRATION_TYPES,
  STYLE_SLIDER_AXES,
  RATES,
} from "../options"
import { ILLUSTRATION_SAMPLE_URLS } from "../sampleImages"

// string[] -> [{ value, label }] for select fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

// Source-files add-on rate (illustration_posting #add_ons_cost = flat $75),
// server-traced via ctx.rates with the RATES seed as the no-live-PriceList
// fallback. Used for both the toggle help copy and the rail line so the two
// always agree (ruling C2 / tuw-app:P-001) — never a hardcoded literal.
const addOnRate = (ctx = {}) => ctx.rates?.illustration_add_on_cost ?? RATES.ILLUSTRATION_ADD_ON_COST

export const illustration = {
  key: "illustration",
  postingKey: "illustration_posting",
  name: "Illustration",

  steps: (values = {}, ctx = {}) => [
    // Step 1 — Brief (illustration__one). The 2-option AI consent is validated in
    // this step server-side, so it renders here.
    {
      id: "brief",
      title: "Illustration brief",
      sub: "Set the count and tell your illustrator about the book.",
      fields: [
        {
          type: "count",
          key: "illustration_count",
          label: "Illustrations",
          required: true,
          min: 1,
          max: 100,
          step: 1,
          unit: "illustrations",
        },
        { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title" },
        { type: "text", key: "subtitle", label: "Subtitle", required: false },
        { type: "text", key: "author_name", label: "Author name", required: true, placeholder: "As it should appear" },
        {
          // Fiction and non-fiction in one grouped list (AU-25). The server
          // whitelist for this field is ILLUSTRATION_NICHES_AND_GENRES.
          type: "select",
          key: "niche",
          label: "Niche / genre",
          required: true,
          blankLabel: "Select Niche/Genre",
          options: NICHE_AND_GENRE_GROUPS,
        },
        {
          type: "select",
          key: "interior_type",
          label: "Interior type",
          required: true,
          blankLabel: "Select interior type",
          options: asOptions(ILLUSTRATION_INTERIOR_TYPES),
        },
        {
          // page_size MUST submit one of the 15 backend ILLUSTRATION_SIZE_VALUES
          // (value_in_array); the design's 6 plain values ('5x8') hard-fail, so the
          // full quote-formatted set is a registered options exception.
          type: "select",
          key: "page_size",
          label: "Page size",
          required: true,
          blankLabel: "Select Page Size",
          options: asOptions(ILLUSTRATION_SIZES),
        },
        {
          type: "select",
          key: "illustration_type",
          label: "Illustration type",
          required: true,
          blankLabel: "Select illustration type",
          options: asOptions(ILLUSTRATION_TYPES),
        },
        {
          type: "textarea",
          key: "character_description",
          label: "Character description",
          required: true,
          rows: 4,
        },
        {
          type: "textarea",
          key: "setting_description",
          label: "Setting description",
          required: true,
          rows: 4,
        },
        {
          type: "text",
          key: "audience",
          label: "Audience",
          required: true,
        },
        {
          type: "textarea",
          key: "examples",
          label: "Links to images for inspiration",
          required: false,
          rows: 3,
        },
        {
          type: "consent",
          key: "ai_consent",
          mode: "2",
          label: "AI assistance",
          required: true,
          help: "Let us know whether your illustrator may use AI tools on this work.",
        },
        {
          type: "file",
          key: "file",
          label: "Requirements file",
          required: false,
          accept: ".doc,.docx,.pdf,.zip",
          placeholder: "Upload a file",
        },
      ],
    },

    // Step 2 — Source files add-on (illustration_posting add_ons).
    {
      id: "addon",
      title: "Source files",
      sub: "Optional, keep the editable files.",
      fields: [
        {
          type: "toggle",
          key: "add_ons",
          label: "Add source files",
          help: `Includes the source files for each illustration. +$${addOnRate(ctx)}.`,
        },
      ],
    },

    // Step 3 — Sample picks (illustration__three — min 3, max 5).
    {
      id: "samples",
      title: "Pick samples",
      sub: "Choose 3-5 illustrations whose style fits your book.",
      fields: [
        {
          type: "gallery",
          key: "illustration_samples",
          required: true,
          min: 3,
          max: 5,
          count: 30,
          noun: "Illustration",
          // Persist backend-legible filenames (illustration_sample_0.png..
          // illustration_sample_29.png) and render the real sample PNGs
          // (Plumbline bugs 1 + 3).
          valueFor: (i) => `illustration_sample_${i}.png`,
          imageFor: (name) => ILLUSTRATION_SAMPLE_URLS[name],
        },
      ],
    },

    // Step 4 — Color palette (illustration__four — min 1, submit KEYS).
    {
      id: "palette",
      title: "Color direction",
      sub: "Choose at least one palette to explore.",
      fields: [
        {
          type: "palette",
          key: "color_palettes",
          required: true,
          min: 1,
        },
      ],
    },

    // Step 5 — Style sliders (illustration__five — at least 2 moved off default).
    {
      id: "style",
      title: "Style sliders",
      sub: "Where should the art sit on each scale?",
      fields: [
        {
          type: "sliders",
          key: "style",
          axes: STYLE_SLIDER_AXES,
          minMoved: 2,
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby: illustration_rate * count + add_ons_cost,
  // illustration_rate = $45/illustration, add_ons_cost = $75 when add_ons on.
  estimate: (values = {}, ctx = {}) => {
    const count = Number(values.illustration_count) || 0
    const rate = ctx.rates?.illustration_cost ?? RATES.ILLUSTRATION_COST
    const lines = []

    if (count > 0) {
      lines.push({
        label: `Illustrations (${count} x $${rate})`,
        amount: money(count * rate),
      })
    }

    if (values.add_ons) {
      lines.push({
        label: "Source files",
        amount: money(ctx.rates?.illustration_add_on_cost ?? RATES.ILLUSTRATION_ADD_ON_COST),
      })
    }

    const total = money(lines.reduce((sum, l) => sum + l.amount, 0))

    return { lines, total, isRange: false, framing: "free-to-post" }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // illustration_posting = [illustration_count, add_ons]. Null until a count is set.
  pricePayload: (values = {}) => {
    const count = Number(values.illustration_count) || 0
    if (count <= 0) return null

    return {
      postingType: "illustration_posting",
      illustrationCount: count,
      addOns: !!values.add_ons,
    }
  },
}
